import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { attestationService } from '@/services/attestationService'
import type { AttestationStatus } from '@/types/attestation'

/**
 * How much of the platform the current session may see.
 *
 * The onboarding funnel is two phases (see `InstitutionalSignupScreen`):
 *
 *   phase 1 — institution identity + the entity attestation → submit
 *   phase 2 — the rest of the profile, the email domain, and the Sumsub KYB
 *
 * Phase 1 lands the attestation in `PENDING_VERIFICATION`, which is what
 * `browse` represents: the user is in, can look around, and sees no numbers.
 * Only a COMPLETE attestation (`has_attestation === true`) unlocks financial
 * data, and only a GREEN KYB produces one.
 *
 *  - `unresolved` — `GET /attestations/status` has not answered yet. NOTHING
 *    that depends on the tier may fetch or render in this state; that is what
 *    stops real numbers flashing on screen for a split second before the gate
 *    applies.
 *  - `anonymous`  — authenticated but not through phase 1. The route guards
 *    send these users into the funnel; no app screen renders for them.
 *  - `browse`     — phase 1 done, verification outstanding. Identity-only data.
 *  - `full`       — COMPLETE attestation. Everything.
 */
export type AccessTier = 'unresolved' | 'anonymous' | 'browse' | 'full'

interface AccessTierValue {
  tier: AccessTier
  /** The raw status, or `null` while unresolved. Prefer `tier` over this. */
  status: AttestationStatus | null
  /**
   * 🔴 The ONE predicate every financial surface must test.
   *
   * False for `unresolved` as well as for `browse`, so a component that guards
   * on it cannot render a number before the server has answered.
   */
  canReadFinancials: boolean
  /** Phase 1 is done — the user may enter the app. */
  canBrowse: boolean
  /**
   * 🔴 The status endpoint FAILED. This is not the same as "the user has no
   * attestation", and the difference is a data-integrity hazard, not a nicety.
   *
   * The fallback below reports `pending_attestation: null`, which is exactly
   * the shape that tells `needsAccountTypeSelection()` a user never started
   * signup. Route on that and a browse-tier user is sent to the account-type
   * picker mid-verification, re-enters the funnel, and mints a DRAFT that
   * SUPERSEDES the attestation they are waiting on. So the guards hold on this
   * flag and offer a retry rather than routing anywhere at all.
   */
  resolutionFailed: boolean
  /** Re-read `GET /attestations/status` and update every consumer. */
  refresh: () => Promise<void>
}

/**
 * Fail CLOSED.
 *
 * The default value is only reachable by a component rendered outside the
 * provider, which is a programming error rather than a state a user can be in.
 * It still has to be the safe answer: a mistake must hide data, never reveal
 * it.
 */
const FALLBACK: AccessTierValue = {
  tier: 'unresolved',
  status: null,
  canReadFinancials: false,
  canBrowse: false,
  resolutionFailed: false,
  refresh: async () => {},
}

const AccessTierContext = createContext<AccessTierValue>(FALLBACK)

/**
 * What the tier is when `GET /attestations/status` itself fails.
 *
 * Fail closed on both axes — not attested and not pending — so a transient
 * error can only ever show LESS than the user is entitled to. This mirrors
 * `FAILED_ATTESTATION_STATUS` in `App.tsx`, which it replaces.
 */
export const FAILED_ATTESTATION_STATUS: AttestationStatus = {
  has_attestation: false,
  is_accredited: false,
  latest_attestation_year: null,
  attestation_source: null,
  organization_id: null,
  pending_attestation: null,
}

/**
 * Derive the tier from the server's answer.
 *
 * 🔴 `pending_attestation !== null` as the phase-1-complete signal is an
 * INTERIM measure. An organization submit always lands in
 * `PENDING_VERIFICATION`, and only a GREEN KYB promotes it to `COMPLETE`, so
 * `has_attestation` is false for the whole of phase 2 and there is no other
 * server-side signal to key on today. The right fix is for
 * `GET /attestations/status` to return explicit `can_read_financials` /
 * `can_trade` booleans so the client stops re-deriving policy from a state
 * machine it does not own. Until then this function is the single place the
 * derivation happens.
 *
 * `EXPIRED` and `SUPERSEDED` need no special case: neither produces
 * `has_attestation === true` (only COMPLETE does), and neither is reported in
 * `pending_attestation` (only PENDING_VERIFICATION is). Both therefore fall
 * through to `anonymous`, and the route guards put the user back at the start
 * of the funnel — which is the correct handling for an attestation that has
 * lapsed or been replaced.
 */
export const deriveTier = (status: AttestationStatus | null): AccessTier => {
  if (status === null) return 'unresolved'
  if (status.has_attestation) return 'full'
  if (status.pending_attestation !== null) return 'browse'
  return 'anonymous'
}

interface AccessTierProviderProps {
  /** Owned by `App`; the status is only fetched for an authenticated session. */
  isAuthenticated: boolean
  children: ReactNode
}

/**
 * Owns the app's single read of `GET /attestations/status`.
 *
 * Before this existed the status was fetched in three places with three
 * different failure policies, and every transition out of onboarding was a
 * `window.location.href` full-document reload — because `App`'s fetch was a
 * one-shot ref-guarded effect and a client-side `navigate()` would carry the
 * stale answer into the route guard and bounce straight back.
 *
 * `refresh()` replaces the reload: completing phase 1 re-reads the status and
 * every consumer (route guards, data hooks, blurred cells) re-renders from the
 * new tier. This is deliberately plain React context rather than React Query:
 * there are two nested `QueryClientProvider`s with different clients
 * (`main.tsx` and `App.tsx`), so an `invalidateQueries` from the wrong scope
 * silently does nothing — and the two data hooks this has to drive
 * (`useOTC`, `useMainScreen`) are hand-rolled `useState`/`useEffect` with no
 * cache key to invalidate anyway.
 */
export const AccessTierProvider = ({ isAuthenticated, children }: AccessTierProviderProps) => {
  const [status, setStatus] = useState<AttestationStatus | null>(null)
  const [resolutionFailed, setResolutionFailed] = useState(false)
  const mountedRef = useRef(true)
  // The one-shot guard for the initial fetch. `refresh()` bypasses it.
  const fetchedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async () => {
    try {
      const next = await attestationService.getAttestationStatus()
      if (mountedRef.current) {
        setStatus(next)
        setResolutionFailed(false)
      }
    } catch (error) {
      console.error('Attestation status check failed:', error)
      if (mountedRef.current) {
        // Fail closed on DATA — nothing gated renders — and raise the flag so
        // the route guards fail closed on ROUTING too, instead of reading the
        // fallback's `pending_attestation: null` as "never started".
        setStatus(FAILED_ATTESTATION_STATUS)
        setResolutionFailed(true)
      }
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      fetchedRef.current = false
      setStatus(null)
      setResolutionFailed(false)
      return
    }
    if (fetchedRef.current) return
    fetchedRef.current = true
    void load()
  }, [isAuthenticated, load])

  const value = useMemo<AccessTierValue>(() => {
    const tier = deriveTier(status)
    return {
      tier,
      status,
      canReadFinancials: tier === 'full',
      canBrowse: tier === 'full' || tier === 'browse',
      resolutionFailed,
      refresh: load,
    }
  }, [status, resolutionFailed, load])

  return <AccessTierContext.Provider value={value}>{children}</AccessTierContext.Provider>
}

export const useAccessTier = () => useContext(AccessTierContext)
